import { createTheme } from '@mui/material/styles';

const primaryMain = '#7851A9';
const primaryDark = '#6A1B9A';

const theme = createTheme({
  palette: {
    primary: {
      main: primaryMain,
      light: '#9C27B0',
      dark: primaryDark,
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#FF6B35',
      light: '#FF8A65',
      dark: '#E64A19',
      contrastText: '#ffffff',
    },
    background: {
      default: '#ffffff',   // Content area → white
      paper: '#ffffff',     // Cards / surfaces → white
    },
    text: {
      primary: '#000000',   // Black text
      secondary: '#3b3b3b', // Grey text
      border: '#E0E0E0',    // Light grey for borders
    },
    success: { main: '#4CAF50' },
    warning: { main: '#FF9800' },
    error: { main: '#F44336' },
    info: { main: '#2196F3' },
  },
  typography: {
    fontFamily: '"Lato", sans-serif',
    fontSize: 13,
    h4: { fontSize: '1.25rem', fontWeight: 300, color: '#000000' },
    h5: { fontSize: '1.1rem', fontWeight: 400, color: '#000000' },
    h6: { fontSize: '0.95rem', fontWeight: 400, color: '#000000' },
    body1: { fontSize: '0.825rem', color: '#000000' },
    body2: { fontSize: '0.775rem', color: '#000000' },
    caption: { fontSize: '0.7rem' },
    button: { fontSize: '0.8rem' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#ffffff',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        },
      },
    },
    // ── Navbar ───────────────────────────────────────────────
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: primaryMain,
          color: '#ffffff',
          boxShadow: 'none',
          borderBottom: '1px solid rgba(255,255,255,0.15)',
        },
      },
    },
    // ── Sidebar ──────────────────────────────────────────────
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          borderRight: '1px solid #E0E0E0',
          boxShadow: 'none',
        },
      },
    },
    // ── Card / Paper ─────────────────────────────────────────
    MuiCard: {
      styleOverrides: {
        root: {
          padding: '10px',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '10px',
          '&:last-child': { paddingBottom: '10px' },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          margin: '1px 6px',
          paddingTop: '5px',
          paddingBottom: '5px',
          color: primaryMain,
          backgroundColor: `rgba(120, 81, 169, 0.08)`,
          border: 'none',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: `rgba(120, 81, 169, 0.15)`,
            color: primaryDark,
          },
          '&.Mui-selected, &[aria-current="page"]': {
            backgroundColor: primaryMain,
            color: '#ffffff',
            fontWeight: 700,
            '&:hover': {
              backgroundColor: primaryDark,
            },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: '32px',
          color: 'inherit',
          '& .MuiSvgIcon-root': { fontSize: '1.15rem' },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: { color: 'inherit', fontSize: '0.8rem' },
      },
    },
    // ── Global Button Overrides ──────────────────────────────
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        size: 'small',
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          boxShadow: 'none',
          fontSize: '0.8rem',
          padding: '4px 12px',
          '&:hover': { boxShadow: 'none' },
          '&:active': { boxShadow: 'none' },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { fontSize: '0.8rem', padding: '8px 12px' },
        head: { fontSize: '0.78rem', fontWeight: 700, padding: '8px 12px' },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontSize: '0.72rem' },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: { fontSize: '0.8rem' },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { fontSize: '0.8rem' },
      },
    },
  },
  shape: {
    borderRadius: 8,
  },
});

export default theme;